import { useEffect, useState, type RefObject } from "react"

export interface useScrollSpyParams {
  /**
   * The scroll container. Sections are searched within it and it is the
   * IntersectionObserver root. Falls back to the document when absent (e.g. the
   * iOS web view, which scrolls the page itself).
   */
  scrollingElement?: RefObject<HTMLElement | null>
  /**
   * CSS selector matching the section nodes to track. Every match must carry an
   * `id`; the id of the topmost intersecting section becomes `activeSection`.
   */
  sectionSelector: string
  offsetPx?: number
}

/**
 * Reports which section is currently scrolled into view, as that section's DOM
 * `id`.
 *
 * Sections are discovered from the DOM by `sectionSelector` and observed /
 * unobserved automatically as they enter and leave the tree. A section that
 * mounts after the hook runs — e.g. an async-loaded Guides tab — is picked up
 * with no imperative registration from the section itself: adding its node to
 * the DOM is the subscription.
 */
const useScrollSpy = ({
  offsetPx = 0,
  scrollingElement,
  sectionSelector,
}: useScrollSpyParams): string | undefined => {
  const [activeSection, setActiveSection] = useState<string | undefined>(
    undefined
  )

  useEffect(() => {
    const scrollRoot = scrollingElement?.current ?? null
    const searchRoot: ParentNode = scrollRoot ?? document
    const mutationRoot: Node = scrollRoot ?? document.body

    const intersecting = new Set<Element>()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersecting.add(entry.target)
          else intersecting.delete(entry.target)
        }

        // The topmost still-intersecting section wins. When nothing intersects
        // (scrolled between sections) we keep the last active one rather than
        // clearing it.
        let topmost: Element | undefined
        let topmostY = Infinity
        for (const el of intersecting) {
          const { top } = el.getBoundingClientRect()
          if (top < topmostY) {
            topmostY = top
            topmost = el
          }
        }
        if (topmost) setActiveSection(topmost.id)
      },
      {
        root: scrollRoot ?? document,
        rootMargin: `${-offsetPx}px 0px 0px 0px`,
        threshold: 0,
      }
    )

    const observed = new Set<Element>()

    const syncSections = () => {
      const present = new Set(searchRoot.querySelectorAll(sectionSelector))
      for (const el of present) {
        if (!observed.has(el)) {
          observer.observe(el)
          observed.add(el)
        }
      }
      for (const el of observed) {
        if (!present.has(el)) {
          observer.unobserve(el)
          observed.delete(el)
          intersecting.delete(el)
        }
      }
    }

    syncSections()

    // Re-scan whenever nodes are added or removed under the scroll root, so
    // late-mounting sections subscribe themselves. querySelectorAll is a cheap
    // native lookup that touches no layout, so running it per mutation batch is
    // fine.
    const mutationObserver = new MutationObserver(syncSections)
    mutationObserver.observe(mutationRoot, { childList: true, subtree: true })

    return () => {
      mutationObserver.disconnect()
      observer.disconnect()
    }
  }, [scrollingElement, sectionSelector, offsetPx])

  return activeSection
}

export default useScrollSpy
